import { useCallback } from 'react';
import { AddressDto } from 'api/actor';
import { ReferentialKey } from 'api/referential';
import { useAppSelector } from 'app/hooks';
import { selectReferentialData } from 'features/referentials/selectors/selectReferentialDataByKey';
import { createMia, CreateMiaCommand } from '../api/miasApi';
import { MiaFormValues } from '../form/MiaFormValues';

import { buildGerantCommand } from 'back-office/shared/components/gerant/gerantFields';
export const useMiaForm = () => {
  const referentialsData = useAppSelector(selectReferentialData);

  const getReferentialId = useCallback((referentialKey: ReferentialKey, code?: string) => {
    if (!code) {
      return undefined;
    }
    return referentialsData[referentialKey].find(ref => ref.code === code)?.id;
  }, [referentialsData]);

  const buildAddressDto = useCallback((values: MiaFormValues): AddressDto => ({
    line1: values.address,
    line2: values.additionalAddress || '',
    zipCode: values.postalCode,
    city: values.city,
    country: values.country,
    formattedAddress: `${values.address},${values.city},${values.country}`,
    latitude: values.latitude || undefined,
    longitude: values.longitude || undefined,
    googlePlaceId: values.googlePlaceId || undefined,
  }), []);

  const createMiaFromForm = useCallback(
    async (values: MiaFormValues) => {
      // Plateformes multiples : chaque mandant en partage au moins une.
      const plateformeIds = (values.plateformeIds ?? []).flatMap((code) => {
        const id = getReferentialId(ReferentialKey.Plateforme, code);
        return id ? [id] : [];
      });

      const command: CreateMiaCommand = {
        companyName: values.companyName,
        address: buildAddressDto(values),
        gerant: buildGerantCommand(values, getReferentialId),
        phone: values.phone,
        registrationNumber: values.registrationNumber,
        capital: values.capital ?? 0,
        rcsCity: values.rcsCity,
        rcsNumber: values.rcsNumber,
        oriasNumber: values.oriasNumber,
        rcp: values.rcp || null,
        complaintEmail: values.complaintEmail,
        mandants: values.mandants,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        username: values.username,
        password: values.password,
        plateformeIds,
      };

      return await createMia(command);
    },
    [buildAddressDto, getReferentialId]
  );

  return { createMia: createMiaFromForm };
};
